import React from 'react';

import {
  Box,
  Card,
  Flex,
  Icon,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { HiOutlineClock, HiOutlineLightningBolt } from 'react-icons/hi';

import { PolicyComplianceIndicator } from './PolicyComplianceIndicator';

/**
 * Formats delay from ISO 8601 duration format (PT5M) to human readable
 */
const getDelayLabel = (delay) => {
  if (delay === 'PT0S') return 'Immediately';

  // Simple parsing for common patterns
  const minutes = delay.match(/PT(\d+)M/);
  if (minutes) return `${minutes[1]} minute${minutes[1] > 1 ? 's' : ''}`;

  const seconds = delay.match(/PT(\d+)S/);
  if (seconds) return `${seconds[1]} second${seconds[1] > 1 ? 's' : ''}`;

  return delay;
};

/**
 * Gets compliance status for a specific policy item by ID
 */
const getComplianceStatusById = (complianceStatus, itemId) => {
  const status = complianceStatus?.find(
    (item) => item.notification_policy_item_id === itemId,
  );
  return status?.is_compliant ?? false;
};

const PolicyComplianceCard = ({ policy, complianceStatus }) => {
  if (!policy?.priorities) return null;

  const priorityOrder = ['HIGH', 'MEDIUM', 'LOW'];
  const orderedPriorities = priorityOrder
    .map((priority) => policy.priorities.find((p) => p.priority === priority))
    .filter(Boolean);

  return (
    <Flex flexDir="column" gap="4">
      <Text textStyle="heading3">Policy compliance overview</Text>
      <Flex
        flexDir="row"
        justifyContent="space-between"
        flexWrap={{ base: 'wrap', lg: 'nowrap' }}
        gap="2"
      >
        {orderedPriorities.map((policyPriority) => {
          return (
            <Card
              key={policyPriority.priority}
              width={{ base: '100%', lg: '33%' }}
              p="3"
              border="1px solid"
              borderColor="stroke.primary"
              boxShadow="sm"
            >
              <Box>
                <Text textStyle="heading3" mb="3">
                  {policyPriority.priority.charAt(0).toUpperCase() +
                    policyPriority.priority.slice(1).toLowerCase()}{' '}
                  Priority Requirements
                </Text>

                <TableContainer>
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Policy</Th>
                        <Th>In Compliance</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {policyPriority.steps?.length > 0 ? (
                        policyPriority.steps.map((step) => {
                          const stepCompliant = getComplianceStatusById(
                            complianceStatus,
                            step.id,
                          );

                          return (
                            <Tr key={step.id}>
                              <Td>
                                <Text
                                  textStyle="body2"
                                  mb="2"
                                  color="content.secondary"
                                >
                                  {step.notification_type?.label ||
                                    'No notification type'}
                                </Text>
                                <Tag variant="roundedOutline">
                                  {step.delay === 'PT0S' ? (
                                    <Icon as={HiOutlineLightningBolt} />
                                  ) : (
                                    <Icon as={HiOutlineClock} />
                                  )}
                                  <Text
                                    mb="0"
                                    pl="1"
                                    fontSize="7"
                                    fontWeight="500"
                                    color="gray.700"
                                  >
                                    {getDelayLabel(step.delay)}
                                  </Text>
                                </Tag>
                              </Td>
                              <Td>
                                <PolicyComplianceIndicator
                                  isCompliant={stepCompliant}
                                  size="sm"
                                  showLabel={false}
                                />
                              </Td>
                            </Tr>
                          );
                        })
                      ) : (
                        <Tr>
                          <Td colSpan={2}>
                            <Text
                              textStyle="body2"
                              color="content.secondary"
                              fontStyle="italic"
                            >
                              No specific requirements for this priority level
                            </Text>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            </Card>
          );
        })}
      </Flex>
    </Flex>
  );
};

PolicyComplianceCard.propTypes = {
  policy: PropTypes.shape({
    priorities: PropTypes.arrayOf(
      PropTypes.shape({
        priority: PropTypes.string.isRequired,
        steps: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            delay: PropTypes.string.isRequired,
            notification_type: PropTypes.shape({
              label: PropTypes.string.isRequired,
              value: PropTypes.string.isRequired,
            }).isRequired,
          }),
        ),
      }),
    ),
  }),
  complianceStatus: PropTypes.arrayOf(
    PropTypes.shape({
      is_compliant: PropTypes.bool.isRequired,
      notification_policy_item_id: PropTypes.string.isRequired,
    }),
  ),
};

export default PolicyComplianceCard;
